import { definePreset } from '@primevue/themes'
import Aura from '@primevue/themes/aura'

/**
 * Custom PrimeVue preset that integrates with our CSS variable system.
 * This preset extends the Aura theme with our color palette and design tokens.
 *
 * @see https://primevue.org/theming/styled/#definepreset
 */
const AppPreset = definePreset(Aura, {
  semantic: {
    // Primary color palette mapped to CSS variables
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },

    colorScheme: {
      light: {
        // Surface colors for light mode
        surface: {
          0: '#ffffff',
          50: '#f8fafc',
          100: '#f1f5f9',
          200: '#e2e8f0',
          300: '#cbd5e1',
          400: '#94a3b8',
          500: '#64748b',
          600: '#475569',
          700: '#334155',
          800: '#1e293b',
          900: '#0f172a',
          950: '#020617',
        },
        // Primary color for light mode
        primary: {
          color: '{primary.600}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.700}',
          activeColor: '{primary.800}',
        },
        // Highlight colors
        highlight: {
          background: '{primary.50}',
          focusBackground: '{primary.100}',
          color: '{primary.700}',
          focusColor: '{primary.800}',
        },
        // Form field colors
        formField: {
          background: '{surface.0}',
          disabledBackground: '{surface.100}',
          filledBackground: '{surface.50}',
          filledHoverBackground: '{surface.100}',
          filledFocusBackground: '{surface.0}',
          borderColor: '{surface.300}',
          hoverBorderColor: '{surface.400}',
          focusBorderColor: '{primary.500}',
          invalidBorderColor: '#ef4444',
          color: '{surface.900}',
          disabledColor: '{surface.500}',
          // surface.400 is 2.56:1 on the white field background, under the 3:1
          // large text floor. Its siblings were already surface.500.
          placeholderColor: '{surface.500}',
          floatLabelColor: '{surface.500}',
          floatLabelFocusColor: '{primary.600}',
          floatLabelActiveColor: '{surface.500}',
          floatLabelInvalidColor: '#ef4444',
          iconColor: '{surface.500}',
          shadow: '0 0 0 1px transparent',
        },
      },
      dark: {
        // Surface ramp runs light to dark in BOTH schemes, which is what Aura
        // assumes. Aura's dark semantics read {surface.0} for text and
        // {surface.900} for content backgrounds, so a ramp inverted only in dark
        // flips both: text resolved to #0f172a and content backgrounds to
        // #ffffff, giving every PrimeVue overlay, list, menu and table body a
        // light-mode appearance inside the dark theme. The DataTable body read as
        // invisible because it sat on a Tailwind dark background.
        //
        // Tailwind is unaffected either way: it reads --color-surface-* from
        // variables.css, a separate scale from PrimeVue's --p-surface-*.
        surface: {
          0: '#ffffff',
          50: '#f8fafc',
          100: '#f1f5f9',
          200: '#e2e8f0',
          300: '#cbd5e1',
          400: '#94a3b8',
          500: '#64748b',
          600: '#475569',
          700: '#334155',
          800: '#1e293b',
          900: '#0f172a',
          950: '#020617',
        },
        // Primary color for dark mode
        // primary.400 is what reads as an accent on a dark surface, and it is too
        // light to carry white: #ffffff on #60a5fa is 2.54:1. Anything drawn ON a
        // primary fill in dark (checkbox ticks, selected chips, badges) takes the
        // near-black end of the ramp instead.
        primary: {
          color: '{primary.400}',
          contrastColor: '{surface.950}',
          hoverColor: '{primary.300}',
          activeColor: '{primary.200}',
        },
        // Highlight colors
        highlight: {
          background: 'rgba(96, 165, 250, 0.16)',
          focusBackground: 'rgba(96, 165, 250, 0.24)',
          color: '{primary.300}',
          focusColor: '{primary.200}',
        },
        // Form field colors for dark mode
        formField: {
          background: '{surface.800}',
          disabledBackground: '{surface.700}',
          filledBackground: '{surface.700}',
          filledHoverBackground: '{surface.600}',
          filledFocusBackground: '{surface.800}',
          borderColor: '{surface.500}',
          hoverBorderColor: '{surface.400}',
          focusBorderColor: '{primary.400}',
          invalidBorderColor: '#f87171',
          color: '{surface.0}',
          disabledColor: '{surface.300}',
          placeholderColor: '{surface.300}',
          floatLabelColor: '{surface.300}',
          floatLabelFocusColor: '{primary.400}',
          floatLabelActiveColor: '{surface.300}',
          floatLabelInvalidColor: '#f87171',
          iconColor: '{surface.300}',
          shadow: '0 0 0 1px transparent',
        },
      },
    },

    // Focus ring styling (Material-inspired)
    focusRing: {
      width: '2px',
      style: 'solid',
      color: '{primary.500}',
      offset: '2px',
      shadow: 'none',
    },

    // Border radius scale
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '4px',
      md: '6px',
      lg: '8px',
      xl: '12px',
    },

    // Transition durations
    transitionDuration: '200ms',
  },

  components: {
    // Button component customization
    button: {
      root: {
        borderRadius: '{borderRadius.md}',
        transitionDuration: '{transitionDuration}',
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              background: '{primary.600}',
              hoverBackground: '{primary.700}',
              activeBackground: '{primary.800}',
              borderColor: '{primary.600}',
              hoverBorderColor: '{primary.700}',
              activeBorderColor: '{primary.800}',
              color: '#ffffff',
            },
            secondary: {
              background: '{surface.100}',
              hoverBackground: '{surface.200}',
              activeBackground: '{surface.300}',
              borderColor: '{surface.300}',
              hoverBorderColor: '{surface.400}',
              activeBorderColor: '{surface.400}',
              color: '{surface.800}',
            },
          },
        },
        // The same blue-600 fill as light, so a PrimeVue button matches
        // BaseButton in both schemes and keeps white text readable.
        dark: {
          root: {
            primary: {
              background: '{primary.600}',
              hoverBackground: '{primary.700}',
              activeBackground: '{primary.800}',
              borderColor: '{primary.600}',
              hoverBorderColor: '{primary.700}',
              activeBorderColor: '{primary.800}',
              color: '#ffffff',
              // Aura reads these from primary.contrastColor, which is near-black
              // in dark so it can sit on the light primary.400 accents.
              hoverColor: '#ffffff',
              activeColor: '#ffffff',
            },
          },
        },
      },
    },

    // Card component customization
    card: {
      root: {
        background: '{surface.0}',
        borderRadius: '{borderRadius.lg}',
        shadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            color: '{surface.900}',
          },
          subtitle: {
            color: '{surface.600}',
          },
        },
        // Written for the old inverted dark ramp, where surface.50 was near black.
        // On the ramp PR 436 restored it rendered a light card inside the dark
        // theme. These mirror Aura's own dark content surface.
        dark: {
          root: {
            background: '{surface.900}',
            color: '{surface.0}',
          },
          subtitle: {
            color: '{surface.400}',
          },
        },
      },
    },

    // Input component customization
    inputtext: {
      root: {
        borderRadius: '{borderRadius.md}',
        transitionDuration: '{transitionDuration}',
      },
    },

    // Select/Dropdown component customization
    select: {
      root: {
        borderRadius: '{borderRadius.md}',
        transitionDuration: '{transitionDuration}',
      },
    },

    // Dialog/Modal component customization
    dialog: {
      root: {
        borderRadius: '{borderRadius.lg}',
      },
      header: {
        padding: '1.25rem 1.5rem 0.75rem 1.5rem',
      },
      content: {
        padding: '0 1.5rem 1.25rem 1.5rem',
      },
      footer: {
        padding: '0 1.5rem 1.25rem 1.5rem',
      },
    },

    // DataTable component customization
    datatable: {
      root: {
        borderRadius: '{borderRadius.lg}',
      },
      // Header and body colours live under colorScheme only. Set at the top level
      // they applied to dark as well, and Aura's white dark text on a surface.50
      // header is 1.06:1.
      colorScheme: {
        light: {
          root: {
            borderColor: '{surface.200}',
          },
          header: {
            background: '{surface.50}',
            borderColor: '{surface.200}',
          },
          headerCell: {
            background: '{surface.50}',
            borderColor: '{surface.200}',
          },
          row: {
            background: '{surface.0}',
            hoverBackground: '{surface.50}',
          },
          bodyCell: {
            borderColor: '{surface.200}',
            selectedBorderColor: '{surface.200}',
          },
        },
        dark: {
          root: {
            borderColor: '{surface.700}',
          },
          header: {
            background: '{surface.800}',
            borderColor: '{surface.700}',
          },
          headerCell: {
            background: '{surface.800}',
            borderColor: '{surface.700}',
          },
          row: {
            background: '{surface.900}',
            hoverBackground: '{surface.800}',
          },
          bodyCell: {
            borderColor: '{surface.700}',
            selectedBorderColor: '{surface.700}',
          },
        },
      },
    },

    // Message/Alert component customization
    message: {
      root: {
        borderRadius: '{borderRadius.md}',
      },
      colorScheme: {
        light: {
          success: {
            background: '#f0fdf4',
            borderColor: '#bbf7d0',
            color: '#166534',
          },
          info: {
            background: '#eff6ff',
            borderColor: '#bfdbfe',
            color: '#1e40af',
          },
          warn: {
            background: '#fffbeb',
            borderColor: '#fde68a',
            color: '#92400e',
          },
          error: {
            background: '#fef2f2',
            borderColor: '#fecaca',
            color: '#991b1b',
          },
        },
        // Aura's dark messages mix the 500 shade into the surface and draw text in
        // that same 500 shade: info is 3.97:1 and error 4.09:1.
        dark: {
          success: {
            background: '{green.950}',
            borderColor: '{green.800}',
            color: '{green.300}',
          },
          info: {
            background: '{blue.950}',
            borderColor: '{blue.800}',
            color: '{blue.300}',
          },
          warn: {
            background: '{amber.950}',
            borderColor: '{amber.800}',
            color: '{amber.300}',
          },
          error: {
            background: '{red.950}',
            borderColor: '{red.800}',
            color: '{red.300}',
          },
        },
      },
    },

    // Badge component customization
    badge: {
      root: {
        borderRadius: '{borderRadius.sm}',
        fontWeight: '500',
      },
    },

    // Menubar component customization
    menubar: {
      root: {
        borderRadius: '0',
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            borderColor: '{surface.200}',
          },
        },
        dark: {
          root: {
            background: '{surface.900}',
            borderColor: '{surface.700}',
          },
        },
      },
    },

    // Toast component customization
    toast: {
      root: {
        borderRadius: '{borderRadius.lg}',
      },
    },

    // SelectButton renders its options as ToggleButtons, and PrimeVue's
    // selectbutton styles read only border radius and the invalid border. Colour
    // tokens set here were never drawn; the togglebutton tokens below are what
    // the theme switcher shows.
    selectbutton: {
      root: {
        borderRadius: '{borderRadius.md}',
      },
    },

    // ToggleButton component customization (used internally by SelectButton)
    togglebutton: {
      colorScheme: {
        // Aura's light label is surface.500 on surface.100, 4.34:1.
        light: {
          root: {
            color: '{surface.600}',
            hoverColor: '{surface.700}',
          },
          icon: {
            color: '{surface.600}',
            hoverColor: '{surface.700}',
          },
        },
        dark: {
          root: {
            background: '{surface.800}',
            checkedBackground: '{surface.800}',
            hoverBackground: '{surface.800}',
            borderColor: '{surface.800}',
            color: '{surface.300}',
            hoverColor: '{surface.200}',
            checkedColor: '{surface.0}',
            checkedBorderColor: '{surface.800}',
          },
          content: {
            checkedBackground: '{surface.600}',
          },
          icon: {
            color: '{surface.300}',
            hoverColor: '{surface.200}',
            checkedColor: '{surface.0}',
          },
        },
      },
    },
  },
})

export default AppPreset
